import { PlusOutlined } from '@ant-design/icons';
import { Button, Form, Input, InputNumber, Modal, Spin, Table } from 'antd';
import cn from 'classnames';

import { generateSpanWithDataTestId } from '@app/shared/utils';

import {
  SUBSCRIPTION_NAME_MAX_LENGTH,
  SUBSCRIPTION_SERVICES_TABLE_SCROLL_X,
} from '../data/constants';
import { useSubscriptionModal } from '../hooks/useSubscriptionModal';
import { useSubscriptionModalEffects } from '../hooks/useSubscriptionModalEffects';

import { SubscriptionServicePicker } from './components/SubscriptionServicePicker';
import { SubscriptionSummary } from './components/SubscriptionSummary';

import type { SubscriptionFormValues, SubscriptionServiceRow } from '../types';
import type { FC } from 'react';

import './SubscriptionModal.scss';

interface SubscriptionModalProps {
  isOpen: boolean;
  /** ID редактируемого абонемента, null — создание нового */
  subscriptionId: number | null;
  onClose: () => void;
}

export const SubscriptionModal: FC<SubscriptionModalProps> = ({
  isOpen,
  subscriptionId,
  onClose,
}) => {
  const {
    form,
    rows,
    columns,
    totals,
    serviceOptions,
    isServicesLoading,
    isSubmitting,
    isEditing,
    isSubscriptionLoading,
    subscriptionToFill,
    fillForm,
    loadSubscription,
    isPickerOpen,
    isServicesErrorShown,
    isMobile,
    openPicker,
    closePicker,
    addService,
    close,
    submit,
  } = useSubscriptionModal({ isOpen, subscriptionId, onClose });

  useSubscriptionModalEffects({
    subscriptionToFill,
    fillForm,
    loadSubscription,
  });

  return (
    <Modal
      title={generateSpanWithDataTestId(
        isEditing ? 'Редактирование абонемента' : 'Новый абонемент',
        'subscription-modal-title'
      )}
      open={isOpen}
      onOk={submit}
      onCancel={close}
      okText="Сохранить абонемент"
      cancelText="Отмена"
      confirmLoading={isSubmitting}
      okButtonProps={{
        'data-testid': 'subscription-submit',
        disabled: isSubscriptionLoading,
      }}
      width={1000}
      className="SubscriptionModal"
      destroyOnHidden
    >
      <Spin spinning={isSubscriptionLoading}>
        <Form<SubscriptionFormValues>
          form={form}
          layout="vertical"
          initialValues={{ Name: '', ValidFromPurchaseDays: null }}
          onFinish={submit}
          className="SubscriptionModal__form"
        >
          <div className="SubscriptionModal__fields">
            <Form.Item
              name="Name"
              label="Наименование"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: 'Введите наименование абонемента',
                },
              ]}
              className="SubscriptionModal__name"
            >
              <Input
                maxLength={SUBSCRIPTION_NAME_MAX_LENGTH}
                placeholder="Например: Солярий 5 минут — 10 посещений"
                data-testid="subscription-name"
              />
            </Form.Item>
            <Form.Item
              name="ValidFromPurchaseDays"
              label="Срок действия, дней"
              tooltip="Отсчитывается с момента продажи. Если не заполнен, абонемент бессрочный"
              className="SubscriptionModal__period"
            >
              <InputNumber
                min={1}
                precision={0}
                placeholder="Бессрочно"
                className="SubscriptionModal__number-input"
                data-testid="subscription-period"
              />
            </Form.Item>
          </div>
        </Form>

        <div className="SubscriptionModal__services">
          <div className="SubscriptionModal__services-header">
            <div>
              <div className="SubscriptionModal__services-title">
                Состав абонемента
              </div>
              <div className="SubscriptionModal__services-hint">
                Цена по прайсу подставляется автоматически из справочника услуг
              </div>
            </div>
            {!isPickerOpen && (
              <Button
                icon={<PlusOutlined />}
                onClick={openPicker}
                className={cn({ 'full-width': isMobile })}
                data-testid="subscription-open-picker"
              >
                Добавить услугу
              </Button>
            )}
          </div>

          {isPickerOpen && (
            <SubscriptionServicePicker
              options={serviceOptions}
              isLoading={isServicesLoading}
              onAdd={addService}
              onCancel={closePicker}
            />
          )}

          <Table<SubscriptionServiceRow>
            columns={columns}
            dataSource={rows}
            rowKey="ClientServiceId"
            size={isMobile ? 'small' : 'middle'}
            scroll={{ x: SUBSCRIPTION_SERVICES_TABLE_SCROLL_X }}
            pagination={false}
            locale={{
              emptyText: 'Добавьте услуги, которые будут входить в абонемент',
            }}
          />

          {isServicesErrorShown && (
            <div className="SubscriptionModal__services-error">
              Добавьте в абонемент хотя бы одну услугу
            </div>
          )}
        </div>

        <SubscriptionSummary totals={totals} />
      </Spin>
    </Modal>
  );
};
